import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js/pure';
import { PayPalButtons } from "@paypal/react-paypal-js";
import { usePlaidLink } from 'react-plaid-link';
import { generateDonationReceipt } from '../utils/donationReceiptGenerator';
import { generateReceiptId, emailDonationReceipt } from '../utils/receiptUtils';
import '../styles/PaymentSelector.css';
import PropTypes from 'prop-types';

/* global ApplePaySession */

const PAYMENT_METHODS = {
  APPLE_PAY: 'APPLE_PAY',
  GOOGLE_PAY: 'GOOGLE_PAY',
  PAYPAL: 'PAYPAL',
  CARD: 'CARD',
  ACH: 'ACH',
  WIRE: 'WIRE',
  CRYPTO: 'CRYPTO'
};

const PaymentSelector = ({ 
  amount,
  cause,
  onPaymentComplete,
  onError 
}) => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);
  const [isGooglePayAvailable, setIsGooglePayAvailable] = useState(false);
  const googlePayButtonRef = useRef(null);
  
  // Initialize Stripe
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

  const handleGooglePayClick = async () => {
    try {
      const stripe = await stripePromise;
      const { error } = await stripe.confirmGooglePayPayment(amount);
      if (error) {
        throw error;
      }
      onPaymentComplete({ type: 'GOOGLE_PAY', amount });
    } catch (error) {
      onError(error);
    }
  };

  const handleCryptoPayment = async () => {
    try {
      const response = await fetch('/api/create-crypto-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount,
          causeId: cause?.id,
          description: `Donation to ${cause?.name || 'Cause and Impact'}`
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create crypto payment');
      }

      if (data.invoiceUrl) {
        window.location.href = data.invoiceUrl;
      } else {
        throw new Error('No invoice URL received');
      }
    } catch (error) {
      onError(error);
    }
  };

  // Check Apple Pay availability
  useEffect(() => {
    if (typeof window !== 'undefined' && 
        'ApplePaySession' in window && 
        window.ApplePaySession) {
      try {
        setIsApplePayAvailable(window.ApplePaySession.canMakePayments());
      } catch (error) {
        console.error('Apple Pay not available:', error);
        setIsApplePayAvailable(false);
      }
    }
  }, []);

  // Initialize Google Pay
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const script = document.createElement('script');
    script.src = 'https://pay.google.com/gp/p/js/pay.js';
    script.async = true;
    script.onload = () => {
      if (window.google?.payments) {
        const googlePayClient = new window.google.payments.api.PaymentsClient({
          environment: process.env.NODE_ENV === 'production' ? 'PRODUCTION' : 'TEST'
        });

        const button = googlePayClient.createButton({
          buttonColor: 'white',
          buttonType: 'donate',
          onClick: handleGooglePayClick,
          allowedPaymentMethods: [{
            type: 'CARD',
            parameters: {
              allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
              allowedCardNetworks: ['MASTERCARD', 'VISA']
            }
          }]
        });

        if (googlePayButtonRef.current) {
          googlePayButtonRef.current.innerHTML = '';
          googlePayButtonRef.current.appendChild(button);
        }
      }
    };
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [amount]);

  // Plaid configuration
  const { open, ready } = usePlaidLink({
    token: process.env.REACT_APP_PLAID_LINK_TOKEN,
    onSuccess: (public_token, metadata) => {
      // Handle successful Plaid link
      handlePlaidSuccess(public_token, metadata);
    },
    onExit: (err, metadata) => {
      if (err) onError(err);
    },
  });

  const handlePlaidSuccess = async (public_token, metadata) => {
    try {
      // Send to your backend to process ACH payment
      const response = await fetch('/api/process-ach-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          public_token,
          metadata,
          amount,
          causeId: cause.id
        })
      });
      
      const result = await response.json();
      if (result.success) {
        onPaymentComplete(result);
      } else {
        onError(new Error(result.message));
      }
    } catch (error) {
      onError(error);
    }
  };

  const renderPaymentMethod = (method) => {
    switch (method) {
      case PAYMENT_METHODS.APPLE_PAY:
        return isApplePayAvailable && (
          <button className="payment-option apple-pay">
            <img 
              src="/icons/apple-pay-white.svg" 
              alt="Apple Pay"
              className="payment-logo"
            />
          </button>
        );

      case PAYMENT_METHODS.GOOGLE_PAY:
        return isGooglePayAvailable && (
          <div 
            ref={googlePayButtonRef} 
            className="payment-option google-pay"
          />
        );

      case PAYMENT_METHODS.PAYPAL:
        return (
          <div className="payment-option paypal">
            <PayPalButtons
              createOrder={(data, actions) => {
                return actions.order.create({
                  purchase_units: [{
                    amount: {
                      value: amount,
                      currency_code: 'USD'
                    },
                    description: `Donation to ${cause.name}`
                  }]
                });
              }}
              onApprove={async (data, actions) => {
                const details = await actions.order.capture();
                onPaymentComplete(details);
              }}
              onError={(err) => onError(err)}
              style={{
                layout: 'horizontal',
                color: 'white',
                shape: 'rect',
                label: 'donate'
              }}
            />
          </div>
        );

      case PAYMENT_METHODS.CARD:
        return (
          <button 
            className="payment-option card"
            onClick={() => setSelectedMethod(PAYMENT_METHODS.CARD)}
          >
            <div className="card-networks">
              <img 
                src="https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg"
                alt="Visa"
                className="network-logo"
              />
              <img 
                src="https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg"
                alt="Mastercard"
                className="network-logo"
              />
            </div>
            <span className="payment-label">Credit/Debit Card</span>
            <div className="powered-by">
              <span>Powered by</span>
              <img 
                src="/icons/stripe.svg"
                alt="Stripe"
                className="stripe-logo"
              />
            </div>
          </button>
        );

      case PAYMENT_METHODS.ACH:
        return (
          <button 
            className="payment-option ach"
            onClick={() => ready && open()}
            disabled={!ready}
          >
            <img 
              src="/icons/plaid.svg"
              alt="Plaid"
              className="plaid-logo"
            />
            <span>Bank Account (ACH)</span>
          </button>
        );

      case PAYMENT_METHODS.WIRE:
        return (
          <button 
            className="payment-option wire"
            onClick={() => setSelectedMethod(PAYMENT_METHODS.WIRE)}
          >
            <i className="fas fa-university" />
            <span>Wire Transfer</span>
          </button>
        );

      case PAYMENT_METHODS.CRYPTO:
        return (
          <button 
            className="payment-option crypto"
            onClick={handleCryptoPayment}
          >
            <div className="crypto-options">
              <img 
                src="/icons/bitcoin.svg"
                alt="Bitcoin"
                className="crypto-logo"
              />
              <span>Cryptocurrency</span>
            </div>
            <div className="powered-by">
              <span>Powered by</span>
              <img 
                src="/icons/bitpay.svg"
                alt="BitPay"
                className="bitpay-logo"
              />
            </div>
          </button>
        );

      default:
        return null;
    }
  };

  // After successful payment processing
  const handlePaymentSuccess = async (paymentDetails) => {
    try {
      // Make sure we have the cause prop
      if (!cause) {
        throw new Error('Cause information is required');
      }

      const donationData = {
        receiptId: generateReceiptId(),
        date: new Date(),
        donor: {
          name: paymentDetails.name,
          address: paymentDetails.address,
          city: paymentDetails.city,
          state: paymentDetails.state,
          zip: paymentDetails.zip
        },
        type: 'cash',
        amount: paymentDetails.amount,
        recurring: paymentDetails.recurring,
        frequency: paymentDetails.frequency,
        donationUrl: window.location.href
      };

      // Generate receipt and save it
      const receipt = await generateDonationReceipt(donationData, cause);
      receipt.save(`${cause.name}-Donation-Receipt-${donationData.receiptId}.pdf`);

      // Email receipt
      await emailDonationReceipt(donationData, cause);

    } catch (error) {
      console.error('Error processing donation:', error);
      onError(error);
    }
  };

  return (
    <div className="payment-selector">
      <h3>Select Payment Method</h3>
      <div className="payment-options">
        {Object.values(PAYMENT_METHODS).map(method => renderPaymentMethod(method))}
      </div>
    </div>
  );
};

PaymentSelector.propTypes = {
  amount: PropTypes.number.isRequired,
  cause: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  onPaymentComplete: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default PaymentSelector;